// utils/ballot.js
//
// The reader's own contests, from /elections/ballot.
//
// /elections/ballot returns everyone on a state's ballot for one election
// date, each candidate carrying the `office` they are running for and, for a
// district-drawn seat, the `district`. That is what lets the Elections tab do
// what the Representatives page does: show the races for the districts the
// reader saved (useHomeLocation -- the same record both pages read, set from an
// address or picked by hand in LocationPicker) instead of every race in the
// state.
//
// What is matched, and what cannot be:
//
//   U.S. Senate         statewide, so every Senate candidate is the reader's.
//   U.S. House          the candidate's district against the saved
//                       congressional district. A one-seat state's at-large
//                       race is everyone's.
//   State legislature   the candidate's district against the saved upper or
//                       lower district, by chamber. Many states' filing lists
//                       do not say which contest a state candidate is in (the
//                       ballot's own notes say so); those candidates carry no
//                       district and cannot be placed, so they are counted
//                       and reported rather than guessed into a race.
//   Statewide offices   governor, attorney general and the like: no district,
//                       so everyone's.
//
// Candidates the state does not list (`not_on_candidate_list`) are never
// shown here: the ballot keeps them apart on purpose.

import { fetchApi2 } from './api2';

// /elections/ballot takes its date as YYYY-MM-DD and rejects anything else
// ("date must be ISO YYYY-MM-DD"). The date handed in comes from the dates
// hook, which can carry a Date object or a full timestamp rather than the
// API's own string, so it is reduced to the calendar day here. A Date is read
// in local time: toISOString would move an evening date west of Greenwich onto
// the next day. Anything that cannot be read is left off, so the request falls
// back to the API's own default rather than failing.
export const toIsoDay = (value) => {
  if (!value) return undefined;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return undefined;
    const pad = (n) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }

  const text = String(value).trim();
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(text);
  if (iso) return iso[1];

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : toIsoDay(parsed);
};

export const fetchBallot = (stateAbbr, date) =>
  fetchApi2('/elections/ballot', { state: stateAbbr, date: toIsoDay(date) });

// ─── Contests ─────────────────────────────────────────────────────────────────

export const CONTEST = {
  US_SENATE: 'us_senate',
  US_HOUSE: 'us_house',
  STATEWIDE: 'statewide',
  STATE_UPPER: 'state_upper',
  STATE_LOWER: 'state_lower',
  OTHER: 'other',
};

// Display order: the races at the top of a real ballot first.
const CONTEST_RANK = {
  [CONTEST.US_SENATE]: 0,
  [CONTEST.US_HOUSE]: 1,
  [CONTEST.STATEWIDE]: 2,
  [CONTEST.STATE_UPPER]: 3,
  [CONTEST.STATE_LOWER]: 4,
  [CONTEST.OTHER]: 5,
};

const lower = (value) => String(value || '').toLowerCase();

// Which kind of contest a candidate is in, from the API's office code first
// (US_House, US_Senate) and the office's own wording otherwise. State offices
// are described in each state's words -- "House of Delegates", "Assembly",
// "Legislature" -- so the chamber is read from those.
export const contestKindOf = (candidate) => {
  const office = lower(candidate?.office);
  const listed = lower(candidate?.office_as_listed);
  const words = `${office} ${listed}`;

  if (office === 'us_senate' || /united states senate|u\.s\. senate/.test(listed)) return CONTEST.US_SENATE;
  if (office === 'us_house' || /united states house|u\.s\. house|representative in congress/.test(listed)) {
    return CONTEST.US_HOUSE;
  }

  const isState = lower(candidate?.level) === 'state' || /state/.test(office);
  if (!isState) return CONTEST.OTHER;

  // "senat" rather than "senate": Indiana's rows read "State Senator", which
  // does not contain "senate", and its senate races were dropped.
  if (/senat|upper/.test(words)) return CONTEST.STATE_UPPER;
  if (/house|assembly|delegates|lower|representative/.test(words)) return CONTEST.STATE_LOWER;
  // Nebraska's one chamber is its Legislature; it is saved as the upper seat.
  if (/legislature|legislative/.test(words)) return CONTEST.STATE_UPPER;

  return candidate?.district ? CONTEST.OTHER : CONTEST.STATEWIDE;
};

// District labels are not numbers -- Idaho has 27A, New Hampshire has
// "Belknap 1" -- and the sources write them differently: padded ("05"),
// ordinal ("20th"), or with the word ("District 9"). Both sides are reduced to
// uppercase letters and digits, with the word and any ordinal suffix removed
// and leading zeros dropped from each run of digits, so they compare equal
// when they name the same seat.
export const normalizeDistrict = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .toUpperCase()
    .replace(/DISTRICT|DIST\.?/g, '')
    .replace(/(\d+)\s*(ST|ND|RD|TH)\b/g, '$1')
    .replace(/[^A-Z0-9]/g, '')
    .replace(/\d+/g, (digits) => String(Number(digits)));
};

const AT_LARGE = new Set(['', '0', 'AL', 'ATLARGE']);

// Whether a saved district and a ballot district name the same seat. Equal
// after normalizing, or -- for a U.S. House seat only -- a saved value that is
// a congressional district's Census GEOID (two digits of state FIPS, two of
// district: Indiana's 5th is 1805) against the ballot's plain number. A
// district picked from a list that carried the GEOID as its id is saved that
// way, and compared as-is it matched no race.
const sameDistrict = (kind, saved, theirs) => {
  const a = normalizeDistrict(saved);
  const b = normalizeDistrict(theirs);
  if (a === b) return true;
  if (kind === CONTEST.US_HOUSE && /^\d{4}$/.test(String(saved).trim())) {
    return String(Number(String(saved).trim().slice(2))) === b;
  }
  return false;
};
const isAtLarge = (value) => AT_LARGE.has(normalizeDistrict(value));

const SAVED_FIELD = {
  [CONTEST.US_HOUSE]: 'congressionalDistrict',
  [CONTEST.STATE_UPPER]: 'stateUpperDistrict',
  [CONTEST.STATE_LOWER]: 'stateLowerDistrict',
};

const LEVEL_KINDS = {
  federal: new Set([CONTEST.US_SENATE, CONTEST.US_HOUSE]),
  state: new Set([CONTEST.STATEWIDE, CONTEST.STATE_UPPER, CONTEST.STATE_LOWER]),
};

/**
 * The reader's contests for one level ('federal' or 'state'), grouped and in
 * ballot order:
 *
 *   contests     [{ key, kind, district, label, candidates }]
 *   unplaced     state candidates with no district, who may be in one of the
 *                reader's races but cannot be shown to be
 *   missingSeat  district kinds the reader has no saved district for
 */
export const contestsForDistricts = (candidates, location, level) => {
  const wanted = LEVEL_KINDS[level] || LEVEL_KINDS.federal;
  const rows = Array.isArray(candidates) ? candidates : [];

  // How many House districts the ballot names: one means the state's seat is
  // at large, whatever the district field says.
  const houseDistricts = new Set(
    rows
      .filter((row) => contestKindOf(row) === CONTEST.US_HOUSE)
      .map((row) => normalizeDistrict(row.district))
  );
  const singleHouseSeat = houseDistricts.size <= 1;

  const groups = new Map();
  let unplaced = 0;
  const missingSeat = new Set();

  rows.forEach((row) => {
    const kind = contestKindOf(row);
    if (!wanted.has(kind)) return;

    const savedField = SAVED_FIELD[kind];
    if (savedField) {
      const saved = location?.[savedField];
      if (!saved) {
        missingSeat.add(kind);
        return;
      }

      const theirs = normalizeDistrict(row.district);
      if (!theirs) {
        // A district race with no district on the row: a state filing list
        // that does not name the contest. Counted, not guessed.
        if (kind === CONTEST.US_HOUSE && singleHouseSeat) {
          // ...except a one-seat state's House race, which is everyone's.
        } else {
          unplaced += 1;
          return;
        }
      } else if (!sameDistrict(kind, saved, row.district)) {
        if (!(kind === CONTEST.US_HOUSE && singleHouseSeat && isAtLarge(row.district))) return;
      }
    }

    // The ballot's own spelling of the district once it has matched: the saved
    // value can be a GEOID (1805), which is no heading for "District 5".
    const district = savedField ? String(row.district || location?.[savedField] || '') : '';
    const key = savedField ? `${kind}:${normalizeDistrict(district)}` : `${kind}:${lower(row.office_as_listed || row.office)}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        kind,
        district,
        office: row.office_as_listed || row.office || '',
        candidates: [],
      });
    }
    groups.get(key).candidates.push(row);
  });

  const contests = [...groups.values()].sort(
    (a, b) =>
      (CONTEST_RANK[a.kind] ?? 9) - (CONTEST_RANK[b.kind] ?? 9) ||
      String(a.office).localeCompare(String(b.office))
  );

  // Incumbents first within a race, then the ballot's own order.
  contests.forEach((contest) => {
    contest.candidates = contest.candidates
      .map((row, index) => ({ row, index }))
      .sort(
        (a, b) =>
          (lower(b.row.incumbent_challenger) === 'incumbent') - (lower(a.row.incumbent_challenger) === 'incumbent') ||
          a.index - b.index
      )
      .map(({ row }) => row);
  });

  return { contests, unplaced, missingSeat: [...missingSeat] };
};